import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Grid } from "@mantine/core";
import { InspectionDto } from "@/types";
import Accordion from "@/components/organisms/AccordionDrawer";
import Text from "@/components/atoms/Text";
import { useInspectionFormat } from "@/components/organisms/InspectionModal/useInspectionFormat";

interface DeviationCostsAccordionProps {
  /** An inspection with at least one deviation. */
  inspection: InspectionDto;
}

/** What the deviations cost the supplier, with the order's weight split, laid out like the modal's cost summary. */
export default function DeviationCostsAccordion({ inspection }: DeviationCostsAccordionProps) {
  const { t } = useTranslation();
  const format = useInspectionFormat();
  const summary = summarizeDeviations(inspection);
  const amount = (value: number) => format.amount(value, summary.currency);

  return (
    <Accordion initiallyOpen title={t("inspections:costs.title")}>
      <Grid p={20} data-testid="inspection-deviation-costs">
        <CostRow
          label={t("inspections:costs.productsValueAdjustment")}
          detail={
            summary.deductedWeightKg > 0
              ? t("inspections:costs.split", {
                  contractWeight: format.number(summary.contractWeightKg),
                  deductedWeight: format.number(summary.deductedWeightKg),
                })
              : null
          }
          amount={amount(summary.productsTotal)}
          testId="inspection-products-cost"
        />
        <CostRow
          label={t("inspections:deviations.servicesTitle")}
          amount={amount(summary.servicesTotal)}
          testId="inspection-services-cost"
        />
        <Grid.Col span={8}>
          <Text display="technical" size="xs" strong tt="uppercase">
            {t("inspections:costs.total")}
          </Text>
        </Grid.Col>
        <Grid.Col span={4}>
          <Text size="md" strong ta="right" data-testid="inspection-total-deviation-amount">
            {amount(summary.total)}
          </Text>
        </Grid.Col>
        {summary.isPreliminary && (
          <Grid.Col span={12}>
            <Text size="xs" color="tertiary" data-testid="inspection-costs-preliminary">
              {t("inspections:drawer.costsPreliminary")}
            </Text>
          </Grid.Col>
        )}
      </Grid>
    </Accordion>
  );
}

const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);

/**
 * Adds up the lines as the backend priced them; nothing is priced here. Rejected deviations aren't invoiced, and
 * pending ones count until they're decided, as in the backend's own weight split. All lines are in the order currency.
 */
const summarizeDeviations = ({ finalWeightKg, deviations }: InspectionDto) => {
  const counted = deviations.filter((deviation) => deviation.confirmationStatus !== "REJECTED");
  const products = counted.filter((deviation) => deviation.kind === "PRODUCT");
  const services = counted.filter((deviation) => deviation.kind === "SERVICE");
  const deductedWeightKg = sum(products.map((deviation) => deviation.weightKg ?? 0));
  const productsTotal = sum(products.map((deviation) => deviation.cost));
  const servicesTotal = sum(services.map((deviation) => deviation.cost));

  return {
    currency: deviations[0].currencyType,
    contractWeightKg: finalWeightKg - deductedWeightKg,
    deductedWeightKg,
    productsTotal,
    servicesTotal,
    total: productsTotal + servicesTotal,
    isPreliminary: deviations.some((deviation) => deviation.confirmationStatus === "PENDING"),
  };
};

interface CostRowProps {
  label: string;
  detail?: ReactNode;
  amount: string;
  testId: string;
}

const CostRow = ({ label, detail, amount, testId }: CostRowProps) => (
  <>
    <Grid.Col span={8}>
      <Text size="sm">{label}</Text>
      {detail && (
        <Text size="xs" color="secondary" data-testid={`${testId}-detail`}>
          {detail}
        </Text>
      )}
    </Grid.Col>
    <Grid.Col span={4}>
      <Text size="sm" ta="right" data-testid={testId}>
        {amount}
      </Text>
    </Grid.Col>
  </>
);
